import { isChannelInactive } from "../../channels/channel-status";
import type { BusinessHours, ChannelSummary, Member } from "../../../api/types";

export type ReadinessItemId = "logo" | "hours" | "channel" | "google" | "team";

export interface ReadinessItem {
  readonly id: ReadinessItemId;
  readonly done: boolean;
  /** Path under `/w/:workspaceId/`. */
  readonly href: string;
}

export interface ReadinessInputs {
  readonly hasLogo: boolean;
  readonly hours: Pick<BusinessHours, "weekly"> | null;
  readonly channels: readonly Pick<ChannelSummary, "status" | "capabilities">[] | null;
  readonly googleConnected: boolean;
  readonly members: readonly Pick<Member, "userId">[] | null;
}

const HREF: Record<ReadinessItemId, string> = {
  logo: "settings/workspace",
  hours: "settings/workspace/hours",
  channel: "channels",
  google: "settings/integrations/google",
  team: "settings/team",
};

/** OWN-365: setup completeness for the settings bar. Pure; inputs come from existing hooks. */
export function computeReadiness(input: ReadinessInputs): {
  readonly percent: number;
  readonly items: readonly ReadinessItem[];
} {
  const hasHours = input.hours
    ? Object.values(input.hours.weekly).some((intervals) => intervals.length > 0)
    : false;
  const hasOfficial = (input.channels ?? []).some(
    (c) => c.capabilities?.templates === true && !isChannelInactive(c),
  );
  const done: Record<ReadinessItemId, boolean> = {
    logo: input.hasLogo,
    hours: hasHours,
    channel: hasOfficial,
    google: input.googleConnected,
    team: (input.members?.length ?? 0) > 1,
  };
  const ids = Object.keys(HREF) as ReadinessItemId[];
  const items = ids.map((id) => ({ id, done: done[id], href: HREF[id] }));
  const percent = Math.round((items.filter((i) => i.done).length / items.length) * 100);
  return { percent, items };
}
